/**
 * UniversalPaymentCheckout.jsx
 * 
 * Universal Payment Modal for Zero-Entry Checkout Experience
 * Handles: Subscriptions (Recurring), Ad Credits (One-Time), Lead Purchases (One-Time)
 * 
 * Features:
 * - Zero-entry: Auto-prefills vendor data (name, email, mobile, gstin)
 * - Hybrid: Supports both Razorpay Subscriptions and Orders
 * - B2B Compliant: GSTIN injection for tax invoices
 * - Saved Cards: Uses razorpayCustomerId when available
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  CreditCard, 
  Shield, 
  Receipt,
  Download,
  RefreshCw,
  IndianRupee,
  Zap,
  Package,
  Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

// Payment type icons
const PAYMENT_ICONS = {
  SUBSCRIPTION: <Zap className="w-5 h-5" />,
  AD_CREDIT: <CreditCard className="w-5 h-5" />,
  LEAD_PURCHASE: <Users className="w-5 h-5" />
};

// Payment type labels
const PAYMENT_LABELS = {
  SUBSCRIPTION: 'Subscription',
  AD_CREDIT: 'Ad Credits',
  LEAD_PURCHASE: 'Lead Pack'
};

// UI States
const CHECKOUT_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed'
};

/**
 * Load Razorpay checkout script dynamically
 */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Universal Payment Checkout Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.userContext - User data for prefill { name, email, mobile, gstin, razorpayCustomerId }
 * @param {Object} props.paymentContext - Payment details { type, amount, planId, referenceId, metaData }
 * @param {Function} props.onSuccess - Success callback with payment result
 * @param {Function} props.onError - Error callback
 */
const UniversalPaymentCheckout = ({
  isOpen,
  onClose,
  userContext = {},
  paymentContext = {},
  onSuccess,
  onError
}) => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  
  // State
  const [checkoutState, setCheckoutState] = useState(CHECKOUT_STATES.INITIALIZING);
  const [orderData, setOrderData] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Merge user context with auth user
  const mergedUserContext = {
    name: userContext.name || user?.name || '',
    email: userContext.email || user?.email || '',
    mobile: userContext.mobile || user?.mobile || user?.phone || '',
    gstin: userContext.gstin || user?.gstin || user?.vendor_profile?.gstin || '',
    razorpayCustomerId: userContext.razorpayCustomerId || user?.razorpay_customer_id || ''
  };

  /**
   * Poll for subscription status after hosted checkout
   */
  const startSubscriptionPolling = useCallback((subscriptionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await apiCall(`/api/razorpay-subscriptions/status/${subscriptionId}`);
        
        if (statusResponse.status === 'active' || statusResponse.status === 'authenticated') {
          // Subscription activated!
          clearInterval(pollInterval);
          setPollingInterval(null);
          setCheckoutState(CHECKOUT_STATES.SUCCESS);
          setPaymentResult({
            subscriptionId: subscriptionId,
            status: statusResponse.status
          });
          
          if (onSuccess) {
            onSuccess({
              subscriptionId: subscriptionId,
              status: statusResponse.status,
              type: 'SUBSCRIPTION'
            });
          }
        } else if (statusResponse.status === 'cancelled' || statusResponse.status === 'halted') {
          // Subscription failed
          clearInterval(pollInterval);
          setPollingInterval(null);
          setCheckoutState(CHECKOUT_STATES.FAILED);
          setErrorMessage('Subscription payment was not completed');
        }
        // For other statuses (created, pending), keep polling
      } catch (error) {
        console.error('Polling error:', error);
        // Don't stop polling on transient errors
      }
    }, 5000); // Poll every 5 seconds
    
    setPollingInterval(pollInterval);
    
    // Stop polling after 10 minutes
    setTimeout(() => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollingInterval(null);
        setCheckoutState(CHECKOUT_STATES.READY);
        toast({
          title: 'Status Check Timeout',
          description: 'Please refresh the page to check your subscription status'
        });
      }
    }, 10 * 60 * 1000);
  }, [apiCall, onSuccess, toast]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  /**
   * Initialize payment - fetch config and create order
   */
  const initializePayment = useCallback(async () => {
    try {
      setCheckoutState(CHECKOUT_STATES.INITIALIZING);
      setErrorMessage('');

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway. Please check your internet connection.');
      }

      // Fetch Razorpay config
      const config = await apiCall('/api/razorpay/config');
      setRazorpayConfig(config);

      // Create order based on payment type
      let orderResponse;
      
      switch (paymentContext.type) {
        case 'SUBSCRIPTION':
          orderResponse = await createSubscriptionOrder();
          break;
        case 'AD_CREDIT':
          orderResponse = await createAdCreditOrder();
          break;
        case 'LEAD_PURCHASE':
          orderResponse = await createLeadPurchaseOrder();
          break;
        default:
          throw new Error(`Unknown payment type: ${paymentContext.type}`);
      }

      setOrderData(orderResponse);
      setCheckoutState(CHECKOUT_STATES.READY);

    } catch (error) {
      console.error('[UniversalPaymentCheckout] Initialization error:', error);
      setErrorMessage(error.message || 'Failed to initialize payment');
      setCheckoutState(CHECKOUT_STATES.FAILED);
    }
  }, [paymentContext, apiCall]);

  /**
   * Create subscription order.
   *
   * iter-230bw — Routing rules:
   *   • Monthly → Razorpay Subscriptions API (recurring) via /create.
   *   • Annual  → Razorpay Orders (one-time) via /create-annual.
   *
   * No silent fallback. If the upstream call fails or returns an
   * unexpected shape, we log + report the failure to the admin team
   * (via /report-create-failure) and surface a clear error so the
   * vendor isn't auto-routed onto a different billing path without
   * knowing.
   */
  const createSubscriptionOrder = async () => {
    const planCode = paymentContext.metaData?.planCode?.toLowerCase() || 'basic';
    const billingCycle = paymentContext.metaData?.billingCycle || 'monthly';

    // Annual path — one-time Razorpay Order, no recurring subscription.
    if (billingCycle === 'annual') {
      let annual;
      try {
        annual = await apiCall('/api/razorpay-subscriptions/create-annual', {
          method: 'POST',
          body: JSON.stringify({ plan_code: planCode, customer_notify: 1 }),
        });
      } catch (err) {
        await reportCreateFailureSilently(planCode, 'annual', err);
        throw new Error(
          err?.message ||
          'Could not create the annual payment. Our team has been alerted.'
        );
      }
      if (!annual?.order_id) {
        await reportCreateFailureSilently(
          planCode, 'annual',
          new Error('create-annual returned no order_id')
        );
        throw new Error(
          'Annual checkout could not be initialised. Our team has been alerted.'
        );
      }
      return {
        ...annual,
        isSubscription: true,
        useHostedCheckout: false,
        order_id: annual.order_id,
        amount: annual.amount,
        razorpay_key_id: annual.razorpay_key_id,
        displayAmount: annual.pricing?.total_amount || annual.amount_rupees || paymentContext.amount,
        displayDescription: `${annual.plan?.name || planCode} Annual Subscription`,
        pricing: annual.pricing,
      };
    }

    // Monthly path — Razorpay Subscriptions API.
    let response;
    try {
      response = await apiCall('/api/razorpay-subscriptions/create', {
        method: 'POST',
        body: JSON.stringify({
          plan_code: planCode,
          billing_cycle: 'monthly',
          customer_notify: 1,
        }),
      });
    } catch (err) {
      await reportCreateFailureSilently(planCode, 'monthly', err);
      throw new Error(
        err?.message ||
        'Could not create the subscription. Our team has been alerted.'
      );
    }

    if (response?.subscription_id) {
      // iter-231-I.6 — Switched from `useHostedCheckout: true` to the
      // in-page Razorpay Checkout JS modal. The merchant account does
      // NOT have the Razorpay Hosted Subscription Page enabled, so
      // every short_url returned the "Hosted page is not available"
      // error. Razorpay Checkout JS supports subscriptions natively:
      // just pass `subscription_id` instead of `order_id` (handled in
      // `openRazorpayCheckout`). short_url is kept as a fallback.
      return {
        ...response,
        isSubscription: true,
        useHostedCheckout: false,
        hostedUrl: response.short_url,
        subscription_id: response.subscription_id,
        displayAmount: response.pricing?.total_amount || response.plan?.price || paymentContext.amount,
        displayDescription: `${paymentContext.metaData?.planName || planCode} Monthly Subscription`,
        razorpay_key_id: response.razorpay_key_id,
        pricing: response.pricing,
      };
    }

    // iter-230bw — Hard fail when subscription create returns no short_url.
    // The old code silently fell back to a one-time order here, which
    // produced ghost vendors who paid but had no Razorpay subscription
    // record on the upstream side.
    await reportCreateFailureSilently(
      planCode, 'monthly',
      new Error('Subscription create returned no short_url')
    );
    throw new Error(
      'Subscription could not be initialised. Our team has been alerted. '
      + 'Please try again in a few minutes.'
    );
  };

  /**
   * Best-effort admin alert. Never throws — we are already in an error
   * path and refuse to mask the original failure.
   */
  const reportCreateFailureSilently = async (planCode, billingCycle, err) => {
    try {
      await apiCall('/api/razorpay-subscriptions/report-create-failure', {
        method: 'POST',
        body: JSON.stringify({
          plan_code: planCode,
          billing_cycle: billingCycle,
          reason: (err && err.message) || 'unknown',
          upstream_response: {
            message: (err && err.message) || null,
            stack: (err && err.stack) ? String(err.stack).slice(0, 1000) : null,
          },
        }),
      });
    } catch (_e) {
      // Swallow — the user-facing error is what matters.
    }
  };

  /**
   * Create ad credit order (one-time)
   */
  const createAdCreditOrder = async () => {
    const response = await apiCall('/api/razorpay/create-order', {
      method: 'POST',
      body: JSON.stringify({
        amount: paymentContext.amount,
        apply_tds: paymentContext.metaData?.applyTds || false
      })
    });
    
    return {
      ...response,
      isSubscription: false,
      displayAmount: response.breakdown?.net_payable || paymentContext.amount,
      displayDescription: `Ad Credits - ₹${paymentContext.amount}`,
      creditsToAdd: response.breakdown?.credits_added || paymentContext.amount
    };
  };

  /**
   * Create lead purchase order (one-time)
   */
  const createLeadPurchaseOrder = async () => {
    const response = await apiCall('/api/platform-payments/lead-pack/create-order', {
      method: 'POST',
      body: JSON.stringify({
        lead_pack_id: paymentContext.referenceId,
        notes: paymentContext.metaData || {}
      })
    });
    
    return {
      ...response,
      isSubscription: false,
      displayAmount: response.amount / 100, // Convert from paise
      displayDescription: response.pack_name || 'Lead Pack Purchase'
    };
  };

  /**
   * Open Razorpay checkout
   */
  const openRazorpayCheckout = () => {
    if (!orderData || !razorpayConfig) {
      toast({
        title: 'Error',
        description: 'Payment not initialized properly',
        variant: 'destructive'
      });
      return;
    }

    // For subscriptions with hosted checkout, redirect to Razorpay hosted page
    if (orderData.useHostedCheckout && orderData.hostedUrl) {
      setCheckoutState(CHECKOUT_STATES.PROCESSING);
      // Open Razorpay hosted subscription page in new tab
      window.open(orderData.hostedUrl, '_blank');
      
      // Close modal and show instructions
      toast({
        title: 'Complete Payment',
        description: 'Please complete your subscription payment in the opened window. This page will refresh automatically.'
      });
      
      // Start polling for subscription status
      startSubscriptionPolling(orderData.subscription_id);
      return;
    }

    setCheckoutState(CHECKOUT_STATES.PROCESSING);

    // Build Razorpay options with Zero-Entry prefill
    const options = {
      key: orderData.razorpay_key_id || razorpayConfig.key_id,
      amount: orderData.amount,
      currency: orderData.currency || 'INR',
      name: 'Aniyor Marketplace',
      description: orderData.displayDescription,
      image: '/logo.png',
      
      // Order ID (for one-time) or Subscription ID (for recurring)
      ...(orderData.isSubscription && orderData.subscription_id
        ? { subscription_id: orderData.subscription_id }
        : { order_id: orderData.order_id || orderData.razorpay_order_id }
      ),

      // ZERO-ENTRY: Auto-prefill from verified vendor data
      prefill: {
        name: mergedUserContext.name,
        email: mergedUserContext.email,
        contact: mergedUserContext.mobile
      },

      // B2B COMPLIANCE: Inject GSTIN for tax invoices
      notes: {
        vendor_id: user?.id,
        gstin: mergedUserContext.gstin,
        payment_type: paymentContext.type,
        reference_id: paymentContext.referenceId || '',
        ...(paymentContext.metaData || {})
      },

      // Enable saved cards if customer ID exists
      ...(mergedUserContext.razorpayCustomerId && {
        customer_id: mergedUserContext.razorpayCustomerId
      }),

      // Theme
      theme: {
        color: '#6366f1',
        backdrop_color: 'rgba(0, 0, 0, 0.7)'
      },

      // Success handler
      handler: async (response) => {
        await handlePaymentSuccess(response);
      },

      // Modal events
      modal: {
        ondismiss: () => {
          setCheckoutState(CHECKOUT_STATES.READY);
          toast({
            title: 'Payment Cancelled',
            description: 'You closed the payment window'
          });
        },
        escape: false,
        animation: true
      }
    };

    // Open Razorpay
    const razorpay = new window.Razorpay(options);
    
    razorpay.on('payment.failed', (response) => {
      handlePaymentFailure(response);
    });

    razorpay.open();
  };

  /**
   * Handle successful payment
   */
  const handlePaymentSuccess = async (razorpayResponse) => {
    try {
      // Verify payment on backend
      let verifyEndpoint;
      let verifyPayload;

      switch (paymentContext.type) {
        case 'SUBSCRIPTION':
          // iter-231-I.6 — Distinguish monthly (subscription_id) from
          // annual (order_id). Monthly auth comes back with a
          // razorpay_subscription_id and a different signature scheme,
          // so it needs the new /verify-payment endpoint.
          if (razorpayResponse.razorpay_subscription_id || orderData.subscription_id) {
            verifyEndpoint = '/api/razorpay-subscriptions/verify-payment';
            verifyPayload = {
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_subscription_id:
                razorpayResponse.razorpay_subscription_id || orderData.subscription_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
            };
          } else {
            verifyEndpoint = '/api/subscription-payments/upgrade/verify';
            verifyPayload = {
              razorpay_order_id: razorpayResponse.razorpay_order_id || orderData.order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              new_plan_id: paymentContext.planId
            };
          }
          break;
        
        case 'AD_CREDIT':
          verifyEndpoint = '/api/razorpay/verify-payment';
          verifyPayload = {
            razorpay_order_id: razorpayResponse.razorpay_order_id,
            razorpay_payment_id: razorpayResponse.razorpay_payment_id,
            razorpay_signature: razorpayResponse.razorpay_signature,
            apply_tds: paymentContext.metaData?.applyTds || false
          };
          break;
        
        case 'LEAD_PURCHASE':
          verifyEndpoint = '/api/platform-payments/lead-pack/verify';
          verifyPayload = {
            razorpay_order_id: razorpayResponse.razorpay_order_id,
            razorpay_payment_id: razorpayResponse.razorpay_payment_id,
            razorpay_signature: razorpayResponse.razorpay_signature
          };
          break;
        
        default:
          throw new Error('Unknown payment type for verification');
      }

      const verifyResponse = await apiCall(verifyEndpoint, {
        method: 'POST',
        body: JSON.stringify(verifyPayload)
      });

      // Update state
      setPaymentResult({
        success: true,
        paymentId: razorpayResponse.razorpay_payment_id,
        orderId: razorpayResponse.razorpay_order_id,
        invoiceNumber: verifyResponse.invoice_number || verifyResponse.gst_voucher_number,
        ...verifyResponse
      });
      setCheckoutState(CHECKOUT_STATES.SUCCESS);

      // Callback
      if (onSuccess) {
        onSuccess({
          paymentId: razorpayResponse.razorpay_payment_id,
          orderId: razorpayResponse.razorpay_order_id,
          ...verifyResponse
        });
      }

    } catch (error) {
      console.error('[UniversalPaymentCheckout] Verification error:', error);
      // Payment succeeded but verification failed - show partial success
      setPaymentResult({
        success: true,
        paymentId: razorpayResponse.razorpay_payment_id,
        verificationPending: true
      });
      setCheckoutState(CHECKOUT_STATES.SUCCESS);
      
      toast({
        title: 'Payment Received',
        description: 'Payment successful but verification pending. Credits will be added shortly.',
        variant: 'warning'
      });
    }
  };

  /**
   * Handle payment failure
   */
  const handlePaymentFailure = (response) => {
    const errorDesc = response.error?.description || 'Payment was not successful';
    setErrorMessage(errorDesc);
    setCheckoutState(CHECKOUT_STATES.FAILED);
    
    if (onError) {
      onError({
        code: response.error?.code,
        description: errorDesc,
        reason: response.error?.reason
      });
    }
  };

  /**
   * Download invoice
   */
  const handleDownloadInvoice = async () => {
    if (!paymentResult?.invoiceNumber) {
      toast({
        title: 'Invoice Not Available',
        description: 'Invoice will be available shortly',
        variant: 'warning'
      });
      return;
    }

    try {
      // Download invoice from backend
      window.open(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${paymentResult.invoiceNumber}/download`,
        '_blank'
      );
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Could not download invoice',
        variant: 'destructive'
      });
    }
  };

  /**
   * Retry payment
   */
  const handleRetry = () => {
    setErrorMessage('');
    initializePayment();
  };

  /**
   * Close and reset
   */
  const handleClose = () => {
    if (checkoutState === CHECKOUT_STATES.PROCESSING) {
      // Don't allow close while processing
      return;
    }
    
    // Reset state
    setCheckoutState(CHECKOUT_STATES.INITIALIZING);
    setOrderData(null);
    setPaymentResult(null);
    setErrorMessage('');
    
    onClose();
  };

  // Initialize on open
  useEffect(() => {
    if (isOpen && paymentContext.type) {
      initializePayment();
    }
  }, [isOpen, paymentContext.type]);

  // Render based on state
  const renderContent = () => {
    switch (checkoutState) {
      case CHECKOUT_STATES.INITIALIZING:
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Securing connection...</p>
          </div>
        );

      case CHECKOUT_STATES.READY:
        return (
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {PAYMENT_ICONS[paymentContext.type]}
                  <span className="font-medium">{PAYMENT_LABELS[paymentContext.type]}</span>
                </div>
                <Badge variant="outline">
                  {orderData?.isSubscription ? 'Recurring' : 'One-Time'}
                </Badge>
              </div>
              
              <div className="text-sm text-muted-foreground">
                {orderData?.displayDescription}
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t" data-testid="checkout-total-row">
                <span className="font-medium">Total Amount</span>
                <span className="text-xl font-bold flex items-center" data-testid="checkout-total-amount">
                  <IndianRupee className="w-5 h-5" />
                  {orderData?.displayAmount?.toLocaleString('en-IN')}
                </span>
              </div>

              {/* iter-231-I.6 — Always show GST breakdown when pricing data is present. */}
              {orderData?.pricing && (
                <div className="text-xs text-muted-foreground space-y-1 pt-1" data-testid="checkout-pricing-breakdown">
                  <div className="flex justify-between">
                    <span>Base Amount</span>
                    <span data-testid="checkout-base-amount">₹{orderData.pricing.base_amount?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({orderData.pricing.gst_rate ?? 18}%)</span>
                    <span data-testid="checkout-gst-amount">₹{orderData.pricing.gst_amount?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Zero-Entry Prefill Info */}
            <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3 border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-green-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-700 dark:text-green-400">Zero-Entry Checkout</p>
                  <p className="text-green-600 dark:text-green-500 text-xs">
                    Your verified details will be auto-filled: {mergedUserContext.email}
                  </p>
                </div>
              </div>
            </div>

            {/* GSTIN Display */}
            {mergedUserContext.gstin && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Receipt className="w-3 h-3" />
                GSTIN: {mergedUserContext.gstin} (B2B Invoice)
              </div>
            )}

            {/* Pay Button */}
            <Button 
              className="w-full h-12 text-lg"
              onClick={openRazorpayCheckout}
              data-testid="universal-checkout-pay-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Pay ₹{orderData?.displayAmount?.toLocaleString('en-IN')}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Secured by Razorpay • 256-bit SSL Encryption
            </p>
          </div>
        );

      case CHECKOUT_STATES.PROCESSING:
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-lg font-medium">Confirming payment...</p>
            <p className="text-sm text-muted-foreground">Please do not close this window</p>
          </div>
        );

      case CHECKOUT_STATES.SUCCESS:
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold">Payment Successful!</h3>
              <p className="text-muted-foreground">
                {paymentContext.type === 'AD_CREDIT' && `₹${orderData?.creditsToAdd || paymentContext.amount} added to your wallet`}
                {paymentContext.type === 'SUBSCRIPTION' && 'Your subscription has been activated'}
                {paymentContext.type === 'LEAD_PURCHASE' && 'Lead pack has been credited'}
              </p>
            </div>

            {/* Receipt Summary */}
            <div className="w-full bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment ID</span>
                <span className="font-mono text-xs">{paymentResult?.paymentId?.slice(0, 20)}...</span>
              </div>
              {paymentResult?.invoiceNumber && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice</span>
                  <span>{paymentResult.invoiceNumber}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium">₹{orderData?.displayAmount?.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 w-full">
              {paymentResult?.invoiceNumber && (
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={handleDownloadInvoice}
                  data-testid="download-invoice-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Invoice
                </Button>
              )}
              <Button 
                className="flex-1"
                onClick={handleClose}
                data-testid="checkout-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        );

      case CHECKOUT_STATES.FAILED:
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold">Payment Failed</h3>
              <p className="text-muted-foreground">{errorMessage || 'Something went wrong'}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 w-full">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleClose}
                data-testid="checkout-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                className="flex-1"
                onClick={handleRetry}
                data-testid="checkout-retry-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => !open && handleClose()}
      // iter-231-N — Radix Dialog defaults to modal={true}, which
      // applies `pointer-events: none` + `inert` to every DOM tree
      // OUTSIDE this Dialog. Razorpay's checkout overlay mounts as a
      // sibling of <body>, so the pointer-events lock makes the
      // Razorpay modal render but silently swallow all clicks (no QR
      // reveal, no method-switch, no close). modal={false} lifts the
      // sibling lock; outside-click-to-close still works via the
      // onPointerDownOutside handler below.
      modal={false}
    >
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => {
          // Prevent closing while processing
          if (checkoutState === CHECKOUT_STATES.PROCESSING) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing while processing
          if (checkoutState === CHECKOUT_STATES.PROCESSING) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {PAYMENT_ICONS[paymentContext.type]}
            {checkoutState === CHECKOUT_STATES.SUCCESS 
              ? 'Payment Complete' 
              : checkoutState === CHECKOUT_STATES.FAILED
                ? 'Payment Failed'
                : `${PAYMENT_LABELS[paymentContext.type] || 'Payment'} Checkout`
            }
          </DialogTitle>
          {checkoutState === CHECKOUT_STATES.READY ? (
            <DialogDescription>
              Complete your payment securely with Razorpay
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              {checkoutState === CHECKOUT_STATES.PROCESSING && 'Payment in progress'}
              {checkoutState === CHECKOUT_STATES.SUCCESS && 'Payment completed successfully'}
              {checkoutState === CHECKOUT_STATES.FAILED && 'Payment did not complete'}
              {checkoutState === CHECKOUT_STATES.INITIALIZING && 'Preparing checkout'}
            </DialogDescription>
          )}
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default UniversalPaymentCheckout;
